import { useState } from 'react'
import { Accordion, Badge, Group, Skeleton, Stack, Text } from '@mantine/core'
import { IconBellOff } from '@tabler/icons-react'
import { DataCard } from '@/components/ui/DataCard'
import { EmptyState } from '@/components/ui/EmptyState'
import { AlertOptInCard } from '@/components/alerts/AlertOptInCard'
import { FeedItem } from '@/pages/notificacoes/FeedItem'
import { useAlertsFeed } from './useAlertsFeed'
import { useCreateMute } from '@/hooks/useAlerts'

/**
 * Tela /alertas — feed de alertas Zabbix agrupado por cliente → ambiente.
 * Cada grupo de ambiente tem um badge "silenciar 2h" que chama useCreateMute
 * com scope_key = "{client}:{environment}".
 */
export function Alertas() {
  const { data, isLoading } = useAlertsFeed()
  const createMute = useCreateMute()
  const groups = data ?? []

  // Controla expansão e seleção dos FeedItems
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<Set<number>>(new Set())

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <Stack gap="md">
      <AlertOptInCard />

      {isLoading ? (
        <Skeleton height={240} />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={IconBellOff}
          title="Nenhum alerta"
          description="Quando o Zabbix disparar, os alertas aparecem aqui agrupados por cliente."
        />
      ) : (
        groups.map((g) => (
          <DataCard flush
            key={g.client}
            title={g.client}
            badge={{ value: g.total }}
          >
            <Accordion
              multiple
              defaultValue={g.environments.map((e) => e.environment)}
            >
              {g.environments.map((env) => (
                <Accordion.Item key={env.environment} value={env.environment}>
                  <Accordion.Control>
                    <Group justify="space-between">
                      <Text fw={500}>{env.environment}</Text>
                      <Badge
                        variant="light"
                        size="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          createMute.mutate({
                            scope_key: `${g.client}:${env.environment}`,
                            hours: 2,
                          })
                        }}
                      >
                        silenciar 2h
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap={4}>
                      {env.items.map((it) => (
                        <FeedItem
                          key={it.event_id}
                          item={it}
                          isSelected={selected.has(it.event_id)}
                          onToggleSelect={toggleSelect}
                          isExpanded={expanded.has(it.event_id)}
                          onToggleExpand={toggleExpand}
                        />
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </DataCard>
        ))
      )}
    </Stack>
  )
}
